import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import apiClient from "../../../utilities/apiClient";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const RequestDocumentModal = ({ open, handleClose, onSubmit }) => {
  const [documentName, setDocumentName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [isRequired, setIsRequired] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingVendors(true);
      apiClient
        .get(apiClient.Urls.vendors_list, "", true)
        .then((res) => {
          if (res?.success) {
            setVendors(res.data || []);
          }
          setLoadingVendors(false);
        })
        .catch(() => {
          setLoadingVendors(false);
        });
    } else {
      // Reset form states
      setDocumentName("");
      setDescription("");
      setDueDate("");
      setIsRequired(true);
      setSelectedVendors([]);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!documentName.trim()) {
      alert("Please enter a document name");
      return;
    }
    if (selectedVendors.length === 0) {
      alert("Please select at least one vendor");
      return;
    }

    const payload = {
      document_name: documentName,
      description,
      due_date: dueDate || null,
      is_required: isRequired,
      vendor_ids: selectedVendors.map((v) => v.id),
    };

    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ fontWeight: 600 }}>Request Document from Vendors</DialogTitle>
      <DialogContent dividers>
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", padding: "0.5rem 0" }}>
          <TextField
            label="Document Name"
            placeholder="e.g., ISO Certification, GST Certificate, Safety Form"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            fullWidth
            required
            size="small"
          />

          <TextField
            label="Description / Instructions"
            placeholder="Describe what needs to be uploaded or any instructions..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
          />

          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                color="primary"
              />
            }
            label="Is this document mandatory?"
          />

          <Autocomplete
            multiple
            id="checkboxes-tags-demo"
            options={vendors}
            disableCloseOnSelect
            loading={loadingVendors}
            getOptionLabel={(option) => {
              const code = option.vendor_code ? ` (${option.vendor_code})` : "";
              return `${option.company_name}${code}`;
            }}
            value={selectedVendors}
            onChange={(event, newValue) => {
              setSelectedVendors(newValue);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.company_name} {option.vendor_code && `(${option.vendor_code})`}
              </li>
            )}
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Vendors"
                placeholder={selectedVendors.length ? "" : "Select one or multiple vendors..."}
                size="small"
              />
            )}
          />
        </div>
      </DialogContent>
      <DialogActions style={{ padding: "1rem" }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Submit Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestDocumentModal;
